/**
 * Client refunds (KAN-40 / KAN-58).
 *
 * The screen is arranged around one distinction the ticket does not make and
 * the money does: **a refund the firm caused is not the same event as a refund
 * the firm was always going to make.** Returning an unearned retainer when a
 * matter closes is the healthy end of an engagement, and a firm doing more of
 * them is not a firm with a problem. So the headline figure is what went back,
 * and the figure beside it is the part attributable to something the firm did —
 * which is the number the epic's "recurring issues" actually means.
 *
 * Two more things are said out loud rather than left to be inferred:
 *
 *  - **How many refunds are unclassified.** They arrive from Clio and
 *    QuickBooks with no reason, because neither has one to give. Until somebody
 *    classifies them every reason total below understates, and a chart that did
 *    not say so would be read as complete.
 *  - **What this dashboard cannot see.** Refunds come from two systems — trust
 *    refunds from Clio, operating-account refunds from QuickBooks — and what
 *    falls outside that, chiefly a QuickBooks credit memo, is footnoted. A
 *    total that quietly omits something is worse than one with a caveat.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  REFUND_APPROVAL_TIER_META,
  REFUND_DECISIONS,
  REFUND_GROUPINGS,
  REFUND_GROUPING_META,
  REFUND_INTERVALS,
  REFUND_INTERVAL_META,
  REFUND_KINDS,
  REFUND_KIND_META,
  REFUND_REASONS,
  REFUND_REASON_META,
  REFUND_STATUSES,
  REFUND_STATUS_META,
  formatBps,
  type ClientRefund,
  type RefundConcentration,
  type RefundCoverage,
  type RefundDecision,
  type RefundGrouping,
  type RefundInterval,
  type RefundKind,
  type RefundLine,
  type RefundOrigin,
  type RefundReason,
  type RefundStatus,
  type RefundSummary,
  type RefundTrendPoint,
} from '@accounting/shared'
import { ApiError, apiFetch } from '../lib/api'
import { formatCount, formatDateTime, formatMoney, formatRelative } from '../lib/format'
import { useSession } from '../lib/session'
import { Alert } from '../ui/Alert'
import { Badge, type Tone } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { Field, Select, TextInput } from '../ui/Field'
import { PageHeader } from '../ui/PageHeader'
import { Stat, StatRow } from '../ui/Stat'
import { EmptyRow, TableWrap, Td, Th } from '../ui/Table'

interface RefundReport {
  summary: RefundSummary
  lines: RefundLine[]
  trend: RefundTrendPoint[]
  interval: RefundInterval
  concentration: RefundConcentration | null
  coverage: RefundCoverage
}

interface RefundPage {
  meta: { total: number; perPage: number; currentPage: number; lastPage: number }
  refunds: ClientRefund[]
}

/**
 * One colour per status, used wherever a status is named.
 *
 * `pending_approval` is the only one drawn as needing attention. Completed is
 * not a problem — the money went back and that is what refunds do — so
 * colouring it as one would leave the row that genuinely wants a decision
 * competing with a screenful of red.
 */
const STATUS_TONES: Record<RefundStatus, Tone> = {
  pending_approval: 'warning',
  approved: 'brand',
  declined: 'neutral',
  completed: 'positive',
}

/**
 * Reasons are coloured by who they are attributable to rather than one colour
 * each. Seven reasons in seven colours is a legend to memorise; three groups is
 * the distinction the screen exists to draw.
 */
const ATTRIBUTION_TONES: Record<string, Tone> = {
  firm: 'critical',
  client: 'accent',
  neither: 'neutral',
}

function reasonTone(reason: RefundReason): Tone {
  return ATTRIBUTION_TONES[REFUND_REASON_META[reason].attribution] ?? 'neutral'
}

/**
 * Trust refunds are marked, billing credits are not.
 *
 * KAN-95 calls trust "the sensitive category" and asks for it to be visually
 * distinguished. Marking only the trust ones — rather than giving both a badge
 * — is what makes the distinction readable at a glance: a column where every
 * row carries a chip is a column nobody scans. Accent rather than red, because
 * a trust refund is a regulated event and not a problem.
 */
const KIND_TONES: Record<RefundKind, Tone> = {
  trust: 'accent',
  billing_credit: 'neutral',
}

/**
 * Groupings a row can be narrowed to.
 *
 * `reason` and `matter` are absent on purpose: both are already available as
 * filter controls above the table, and a Focus button that duplicates a select
 * two feet away is a second way to reach one state.
 */
const FOCUSABLE: readonly RefundGrouping[] = ['client', 'attorney', 'matter_type']

/**
 * Narrowing the grouped table to the groups worth a look.
 *
 * Applied in the browser rather than sent to the server: the report already
 * carries every group with its counts, so each of these is a question about
 * figures on screen, and asking the server again would recompute the whole
 * dashboard to hide some rows.
 */
const GROUP_FILTERS = {
  all: { label: 'Every group', test: () => true },
  firm: { label: 'Firm caused some', test: (line: RefundLine) => line.firmAttributedMinor > 0 },
  committed: { label: 'Has committed', test: (line: RefundLine) => line.outstandingCount > 0 },
  unclassified: {
    label: 'Needs a reason',
    test: (line: RefundLine) => line.unclassifiedCount > 0,
  },
  unapproved: {
    label: 'Unapproved refunds',
    test: (line: RefundLine) => line.unapprovedCount > 0,
  },
  trust: { label: 'Trust money returned', test: (line: RefundLine) => line.byKind.trust > 0 },
} satisfies Record<string, { label: string; test: (line: RefundLine) => boolean }>

type GroupFilter = keyof typeof GROUP_FILTERS

const GROUP_PAGE_SIZE = 10

/** Where a listed refund came from. Mirrors `LIST_SOURCES` on the API. */
const LIST_SOURCES = {
  clio: 'From Clio',
  qbo: 'From QuickBooks',
  manual: 'Recorded here',
} as const

type ListSource = keyof typeof LIST_SOURCES

/** A value that settles once typing pauses, so a search is one request and not one per key. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delayMs)
    return () => window.clearTimeout(timer)
  }, [value, delayMs])
  return settled
}

export function Refunds() {
  const { may } = useSession()
  const mayManage = may('refunds.manage')

  const [grouping, setGrouping] = useState<RefundGrouping>('client')
  const [interval, setInterval] = useState<RefundInterval>('month')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [status, setStatus] = useState<RefundStatus | ''>('')
  const [reason, setReason] = useState<RefundReason | ''>('')
  const [kind, setKind] = useState<RefundKind | ''>('')
  /**
   * Narrowing to one client or one attorney, set by focusing a row — or, for a
   * client, by the picker above, which offers only the clients a refund has
   * been recorded against rather than every client in the firm.
   */
  const [focus, setFocus] = useState<{
    kind: 'client' | 'attorney' | 'matter_type'
    id: string
    label: string
  } | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [recording, setRecording] = useState(false)
  /** Which of the two tables under the client picker is showing. */
  const [tab, setTab] = useState<'groups' | 'refunds'>('groups')

  const [groupSearch, setGroupSearch] = useState('')
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('all')
  const [groupPage, setGroupPage] = useState(1)

  const [listSearch, setListSearch] = useState('')
  const settledListSearch = useDebounced(listSearch.trim(), 300)
  const [listSource, setListSource] = useState<ListSource | ''>('')
  const [listPage, setListPage] = useState(1)

  const refundClients = useQuery({
    queryKey: ['refund-clients'],
    queryFn: () => apiFetch<{ clients: { id: string; name: string }[] }>('/refunds/clients'),
  })
  const [clientText, setClientText] = useState('')

  /** Kept in step when a client is focused, or cleared, from somewhere other than the picker. */
  useEffect(() => {
    if (focus?.kind === 'client') setClientText(focus.label)
    else if (!focus) setClientText('')
  }, [focus])

  /**
   * Applies once the text names a client exactly — which is what choosing from
   * the suggestions produces — and clears once the box is emptied. A half-typed
   * name leaves the current narrowing alone rather than flickering the page.
   */
  function pickClient(text: string) {
    setClientText(text)
    const needle = text.trim().toLowerCase()
    if (!needle) {
      if (focus?.kind === 'client') setFocus(null)
      return
    }
    const match = refundClients.data?.clients.find(
      (candidate) => candidate.name.toLowerCase() === needle
    )
    if (match) setFocus({ kind: 'client', id: match.id, label: match.name })
  }

  const filters = useMemo(() => {
    const params = new URLSearchParams({ groupBy: grouping, interval })
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    if (status) params.set('status', status)
    if (reason) params.set('reason', reason)
    if (kind) params.set('kind', kind)
    /**
     * Matter type is narrowed by focusing a row rather than by a control of its
     * own: practice areas are an open set the tenant names, so a select would
     * have to be populated from somewhere, and the matter-type grouping already
     * *is* the list of the ones with refunds against them.
     */
    if (focus?.kind === 'client') params.set('clientId', focus.id)
    if (focus?.kind === 'attorney') params.set('attorneyId', focus.id)
    if (focus?.kind === 'matter_type') params.set('matterType', focus.id)
    return params
  }, [grouping, interval, from, to, status, reason, kind, focus])

  const key = filters.toString()

  const report = useQuery({
    queryKey: ['refunds', key],
    queryFn: () => apiFetch<RefundReport>(`/refunds?${key}`),
  })

  /** The rows, under the same filters minus the grouping the table applies. */
  const listFilters = useMemo(() => {
    const params = new URLSearchParams(filters)
    params.delete('groupBy')
    params.delete('interval')
    if (settledListSearch) params.set('q', settledListSearch)
    if (listSource) params.set('source', listSource)
    return params.toString()
  }, [filters, settledListSearch, listSource])

  /**
   * Back to the first page whenever what is being paged changes. Page 4 of the
   * old filter is not a place in the new one, and landing on an empty page
   * reads as "no results".
   */
  useEffect(() => {
    setListPage(1)
    setExpanded(null)
  }, [listFilters])

  useEffect(() => {
    setGroupPage(1)
  }, [key, groupSearch, groupFilter])

  const listQuery = `${listFilters}${listFilters ? '&' : ''}page=${listPage}`

  const refunds = useQuery({
    queryKey: ['refunds-list', listQuery],
    queryFn: () => apiFetch<RefundPage>(`/refunds/list?${listQuery}`),
    placeholderData: (previous) => previous,
  })

  const summary = report.data?.summary ?? null
  const lines = report.data?.lines ?? []
  const rows = refunds.data?.refunds ?? []
  const listMeta = refunds.data?.meta

  const visibleLines = useMemo(() => {
    const needle = groupSearch.trim().toLowerCase()
    const test = GROUP_FILTERS[groupFilter].test
    return lines.filter((line) => {
      if (!test(line)) return false
      if (!needle) return true
      return [groupLabelFor(line, grouping), line.clientName, line.attorneyName].some((value) =>
        value?.toLowerCase().includes(needle)
      )
    })
  }, [lines, groupSearch, groupFilter, grouping])

  const groupLastPage = Math.max(1, Math.ceil(visibleLines.length / GROUP_PAGE_SIZE))
  const groupCurrentPage = Math.min(groupPage, groupLastPage)
  const pagedLines = visibleLines.slice(
    (groupCurrentPage - 1) * GROUP_PAGE_SIZE,
    groupCurrentPage * GROUP_PAGE_SIZE
  )

  /** Focusing a row only means anything under the grouping that produced it. */
  function changeGrouping(next: RefundGrouping) {
    setGrouping(next)
    setExpanded(null)
    /** A client stays picked under any grouping; it can be chosen from the picker without one. */
    if (focus && focus.kind !== 'client' && next !== focus.kind) setFocus(null)
  }

  return (
    <>
      <PageHeader
        title="Client refunds"
        description="Money returned to clients: how much, why, who approved it, and how much of it the firm caused. Every refund drills down to the invoice or trust movement it came off."
        actions={
          mayManage && (
            <Button variant="accent" onClick={() => setRecording((open) => !open)}>
              {recording ? 'Cancel' : 'Record a refund'}
            </Button>
          )
        }
      />

      {report.error && (
        <Alert tone="error" className="mb-4">
          {messageFor(report.error)}
        </Alert>
      )}

      {recording && mayManage && (
        <RecordRefund onDone={() => setRecording(false)} />
      )}

      <Card className="mb-6">
        <CardBody>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <Field
              id="refund-from"
              label="From"
              hint="By the date each refund is dated by — when the money moved, or when it was recorded if it has not."
            >
              <TextInput
                id="refund-from"
                type="date"
                value={from}
                max={to || undefined}
                onChange={(event) => setFrom(event.target.value)}
              />
            </Field>

            <Field id="refund-to" label="To" hint="Leave both blank for the trailing twelve months.">
              <TextInput
                id="refund-to"
                type="date"
                value={to}
                min={from || undefined}
                onChange={(event) => setTo(event.target.value)}
              />
            </Field>

            <Field
              id="refund-interval"
              label="Trend by"
              hint="Widened automatically if the range would draw more bars than can be read."
            >
              <Select
                id="refund-interval"
                value={interval}
                onChange={(event) => setInterval(event.target.value as RefundInterval)}
              >
                {REFUND_INTERVALS.map((option) => (
                  <option key={option} value={option}>
                    {REFUND_INTERVAL_META[option].label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              id="refund-group"
              label="Group by"
              hint={REFUND_GROUPING_META[grouping].description}
            >
              <Select
                id="refund-group"
                value={grouping}
                onChange={(event) => changeGrouping(event.target.value as RefundGrouping)}
              >
                {REFUND_GROUPINGS.map((option) => (
                  <option key={option} value={option}>
                    {REFUND_GROUPING_META[option].label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field id="refund-status" label="Status" hint="Every status, unless you pick one.">
              <Select
                id="refund-status"
                value={status}
                onChange={(event) => setStatus(event.target.value as RefundStatus | '')}
              >
                <option value="">All statuses</option>
                {REFUND_STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {REFUND_STATUS_META[option].label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field id="refund-reason" label="Reason" hint="Every reason, unless you pick one.">
              <Select
                id="refund-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value as RefundReason | '')}
              >
                <option value="">All reasons</option>
                {REFUND_REASONS.map((option) => (
                  <option key={option} value={option}>
                    {REFUND_REASON_META[option].label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              id="refund-kind"
              label="Account"
              hint={
                kind
                  ? REFUND_KIND_META[kind].description
                  : 'Trust money and operating-account credits are different events. Pick one to read it on its own.'
              }
            >
              <Select
                id="refund-kind"
                value={kind}
                onChange={(event) => setKind(event.target.value as RefundKind | '')}
              >
                <option value="">Both</option>
                {REFUND_KINDS.map((option) => (
                  <option key={option} value={option}>
                    {REFUND_KIND_META[option].label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          {focus && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-ink-700">
              <span>
                Narrowed to{' '}
                {focus.kind === 'client'
                  ? 'client'
                  : focus.kind === 'attorney'
                    ? 'responsible attorney'
                    : 'matter type'}
              </span>
              <Badge tone="brand">{focus.label}</Badge>
              <Button size="sm" variant="ghost" onClick={() => setFocus(null)}>
                Show the whole firm
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {report.isPending && <p className="text-sm text-ink-600">Loading refunds…</p>}

      {summary && (
        <>
          <StatRow className="mb-3">
            <Stat
              label="Refunded"
              value={formatMoney(summary.refundedMinor)}
              hint={`${formatCount(summary.refundedCount)} ${
                summary.refundedCount === 1 ? 'refund' : 'refunds'
              } to ${formatCount(summary.clientCount)} ${
                summary.clientCount === 1 ? 'client' : 'clients'
              }. Money that actually went back — nothing pending is counted here.`}
            />
            <Stat
              label="Firm caused"
              value={formatMoney(summary.firmAttributedMinor)}
              tone={summary.firmAttributedMinor > 0 ? 'warning' : 'muted'}
              hint="Billing errors and service credits. Returning an unearned retainer is not counted: it is how an engagement ends, not a defect."
            />
            <Stat
              label="Awaiting a decision"
              value={formatMoney(summary.pendingApprovalMinor)}
              tone={summary.pendingApprovalCount > 0 ? 'warning' : 'muted'}
              hint={
                <>
                  {summary.approvedAwaitingCount > 0
                    ? `${formatCount(summary.pendingApprovalCount)} pending. A further ${formatMoney(
                        summary.approvedAwaitingMinor
                      )} is approved and waiting for the movement to arrive from Clio.`
                    : `${formatCount(summary.pendingApprovalCount)} recorded here and not yet decided. No money has moved.`}
                  {/*
                    * The age, not just the count — "2 pending" reads the same
                    * whether they have waited an hour or all quarter, and which
                    * of those it is was the question.
                    */}
                  {summary.oldestPendingDays !== null && (
                    <> Oldest has waited {formatCount(summary.oldestPendingDays)} days.</>
                  )}
                  {summary.oldestApprovedDays !== null && (
                    <>
                      {' '}
                      Oldest approved has waited {formatCount(summary.oldestApprovedDays)} days for
                      Clio.
                    </>
                  )}
                </>
              }
            />
            <Stat
              label="Refund rate"
              value={
                summary.refundRateBps === null ? 'No receipts' : formatBps(summary.refundRateBps)
              }
              tone={summary.refundRateBps === null ? 'muted' : 'default'}
              hint={
                summary.refundRateBps === null
                  ? 'Nothing was received in this window, so there is no denominator. Shown as unmeasurable rather than as zero.'
                  : `Of ${formatMoney(summary.receiptsMinor)} received over the same window.`
              }
            />
          </StatRow>

          {/*
            * The regulated split, on its own row rather than folded into the
            * headline. One of these is client money leaving the IOLTA account
            * and the other is an accounting correction; a single "refunded"
            * total covering both is what would hide a rise in the one that
            * carries bar-rule consequences.
            */}
          <StatRow className="mb-6 lg:grid-cols-2">
            <Stat
              label="Trust money returned"
              value={formatMoney(summary.byKind.trust)}
              tone={summary.byKind.trust > 0 ? 'warning' : 'muted'}
              hint={`${formatCount(summary.trustRefundCount)} ${
                summary.trustRefundCount === 1 ? 'refund' : 'refunds'
              } of unearned client funds out of the IOLTA account. A regulated action, and each one carries the trust movement that paid it.`}
            />
            <Stat
              label="Billing credits"
              value={formatMoney(summary.byKind.billing_credit)}
              tone="muted"
              hint="Money the firm had already earned and invoiced, given back out of the operating account. An accounting correction rather than a trust matter."
            />
          </StatRow>

          {/*
            * The trend and the control findings side by side from xl up, so the
            * findings do not push the chart and the tables below the fold. The
            * trend takes the whole row when there is nothing to report.
            */}
          <div
            className={`mb-6 grid items-start gap-6 ${
              summary.elevatedUnapprovedCount > 0 ||
              summary.unapprovedCompletedCount > 0 ||
              summary.unclassifiedCount > 0
                ? 'xl:grid-cols-2'
                : ''
            }`}
          >
            <Card className="min-w-0">
              <CardHeader
                title="Trend"
                description={`${
                  REFUND_INTERVAL_META[report.data?.interval ?? interval].label
                }, by when the money moved. Only completed refunds appear — a request that has not been processed has no date it happened.`}
                actions={
                  report.data && report.data.interval !== interval ? (
                    <Badge tone="neutral">Widened to fit</Badge>
                  ) : undefined
                }
              />
              <CardBody>
                <TrendChart points={report.data?.trend ?? []} />
              </CardBody>
            </Card>

            <div className="flex min-w-0 flex-col gap-6 empty:hidden">
              {summary.elevatedUnapprovedCount > 0 && (
                <Alert
                  tone="error"
                  title="Large refunds completed with no approval recorded"
                >
                  {formatMoney(summary.elevatedUnapprovedMinor)} across{' '}
                  {formatCount(summary.elevatedUnapprovedCount)}{' '}
                  {summary.elevatedUnapprovedCount === 1 ? 'refund' : 'refunds'} at or above the{' '}
                  {formatMoney(summary.elevatedThresholdMinor)} elevated threshold went back with nobody
                  deciding on it here. A small credit going out unapproved is untidy; at this size it is
                  the finding. The threshold is a starting default and is named here so it can be
                  checked — Finance has not yet set the firm's own.
                </Alert>
              )}

              {summary.unapprovedCompletedCount > 0 && (
                <Alert tone="warning" title="Processed without an approval here">
                  {formatMoney(summary.unapprovedCompletedMinor)} across{' '}
                  {formatCount(summary.unapprovedCompletedCount)}{' '}
                  {summary.unapprovedCompletedCount === 1 ? 'refund' : 'refunds'} went back to clients
                  with no decision recorded in this platform. That is the ordinary case for a refund
                  issued directly in Clio, which has no approver to sync — it is reported because it is
                  the control question a refund dashboard exists to answer, not because something has
                  gone wrong. Recording a refund here before it is processed is what puts a name against
                  it.
                </Alert>
              )}

              {summary.unclassifiedCount > 0 && (
                <Alert tone="info" title="Some refunds have no reason recorded">
                  {formatCount(summary.unclassifiedCount)}{' '}
                  {summary.unclassifiedCount === 1 ? 'refund' : 'refunds'} worth{' '}
                  {formatMoney(summary.unclassifiedMinor)} arrived from Clio, which records no reason
                  for a trust movement. Nothing here guesses one from the description, so until somebody
                  classifies them every reason total below — and the firm-caused figure above —
                  understates by up to that amount.
                </Alert>
              )}
            </div>
          </div>

          {report.data?.concentration && (
            <Concentration concentration={report.data.concentration} />
          )}

          {/*
            * The two tables as tabs under one client picker. Side by side each
            * was too narrow to read; as tabs each gets the full width, and
            * picking a client narrows both of them — and the figures above —
            * at once.
            */}
          <Card className="mb-6">
            <CardBody className="border-b border-ink-100">
              <Field
                id="refund-client"
                label="Client"
                hint="Type to search the clients with a refund. Clear it for the whole firm."
                className="max-w-xl"
              >
                <TextInput
                  id="refund-client"
                  type="search"
                  list="refund-client-options"
                  autoComplete="off"
                  placeholder="Every client"
                  value={clientText}
                  onChange={(event) => pickClient(event.target.value)}
                />
                <datalist id="refund-client-options">
                  {refundClients.data?.clients.map((candidate) => (
                    <option key={candidate.id} value={candidate.name} />
                  ))}
                </datalist>
              </Field>
            </CardBody>

            <div
              role="tablist"
              aria-label="Refund tables"
              className="flex gap-1 border-b border-ink-200 px-5"
            >
              <Tab active={tab === 'groups'} onClick={() => setTab('groups')}>
                By {REFUND_GROUPING_META[grouping].label.toLowerCase()}
                <Badge tone="neutral">
                  {visibleLines.length === lines.length
                    ? formatCount(lines.length)
                    : `${formatCount(visibleLines.length)} of ${formatCount(lines.length)}`}
                </Badge>
              </Tab>
              <Tab active={tab === 'refunds'} onClick={() => setTab('refunds')}>
                Refunds
                {listMeta && <Badge tone="neutral">{formatCount(listMeta.total)}</Badge>}
              </Tab>
            </div>

            {tab === 'groups' && (
              <div role="tabpanel">
                <p className="px-5 pt-3 text-xs text-ink-600">
                  Refunded is money that went back. Committed is recorded and not yet processed, and
                  the two are never added together.
                </p>
                <Toolbar>
                  <TextInput
                    type="search"
                    aria-label={`Search by ${REFUND_GROUPING_META[grouping].label.toLowerCase()}`}
                    placeholder={`Search ${REFUND_GROUPING_META[grouping].label.toLowerCase()}…`}
                    value={groupSearch}
                    onChange={(event) => setGroupSearch(event.target.value)}
                    className="mt-0 min-w-0 flex-1"
                  />
                  <Select
                    aria-label="Show groups"
                    value={groupFilter}
                    onChange={(event) => setGroupFilter(event.target.value as GroupFilter)}
                    className="mt-0 w-auto"
                  >
                    {(Object.keys(GROUP_FILTERS) as GroupFilter[]).map((option) => (
                      <option key={option} value={option}>
                        {GROUP_FILTERS[option].label}
                      </option>
                    ))}
                  </Select>
                </Toolbar>
                <TableWrap>
                  <thead>
                    <tr>
                      <Th>{REFUND_GROUPING_META[grouping].label}</Th>
                      <Th className="text-right">Refunded</Th>
                      <Th className="text-right">Share</Th>
                      <Th className="text-right">Firm caused</Th>
                      <Th className="text-right">Committed</Th>
                      <Th>Last refund</Th>
                      <Th>Gaps</Th>
                      {FOCUSABLE.includes(grouping) && <Th />}
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 && (
                      <EmptyRow colSpan={8}>
                        No refunds in this window under these filters.
                      </EmptyRow>
                    )}

                    {lines.length > 0 && visibleLines.length === 0 && (
                      <EmptyRow colSpan={8}>Nothing matches that search or filter.</EmptyRow>
                    )}

                    {pagedLines.map((line) => (
                      <GroupRow
                        key={line.groupId ?? '__unattributed__'}
                        line={line}
                        grouping={grouping}
                        onFocus={
                          FOCUSABLE.includes(grouping)
                            ? () => {
                                setFocus(
                                  line.groupId
                                    ? {
                                        kind: grouping as 'client' | 'attorney' | 'matter_type',
                                        id: line.groupId,
                                        label: line.groupLabel,
                                      }
                                    : null
                                )
                                /** Narrowing to a row is asking to see its refunds. */
                                setTab('refunds')
                              }
                            : undefined
                        }
                      />
                    ))}
                  </tbody>
                </TableWrap>
                <Pager
                  currentPage={groupCurrentPage}
                  lastPage={groupLastPage}
                  onChange={setGroupPage}
                />
              </div>
            )}

            {tab === 'refunds' && (
              <div role="tabpanel">
                <p className="px-5 pt-3 text-xs text-ink-600">
                  Pending decisions first, then by date. Open a row for the invoice or trust movement
                  behind it.
                </p>
                <Toolbar>
                  <TextInput
                    type="search"
                    aria-label="Search refunds"
                    placeholder="Search client, matter, invoice or note…"
                    value={listSearch}
                    onChange={(event) => setListSearch(event.target.value)}
                    className="mt-0 min-w-0 flex-1"
                  />
                  <Select
                    aria-label="Source"
                    value={listSource}
                    onChange={(event) => setListSource(event.target.value as ListSource | '')}
                    className="mt-0 w-auto"
                  >
                    <option value="">Every source</option>
                    {(Object.keys(LIST_SOURCES) as ListSource[]).map((option) => (
                      <option key={option} value={option}>
                        {LIST_SOURCES[option]}
                      </option>
                    ))}
                  </Select>
                </Toolbar>

                {refunds.error && (
                  <CardBody>
                    <Alert tone="error">{messageFor(refunds.error)}</Alert>
                  </CardBody>
                )}

                <TableWrap>
                  <thead>
                    <tr>
                      <Th>Client</Th>
                      <Th className="text-right">Amount</Th>
                      <Th>Reason</Th>
                      <Th>Status</Th>
                      <Th>Approver</Th>
                      <Th>Dated</Th>
                      <Th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && !refunds.isPending && (settledListSearch || listSource) && (
                      <EmptyRow colSpan={7}>Nothing matches that search or source.</EmptyRow>
                    )}

                    {rows.length === 0 && !refunds.isPending && !settledListSearch && !listSource && (
                      <EmptyRow colSpan={7}>
                        Nothing to show. Refunds arrive with the Clio trust sync; until it has run there
                        is nothing here to report —{' '}
                        <Link to="/integrations" className="underline">
                          integration status
                        </Link>{' '}
                        shows what is waiting on credentials.
                      </EmptyRow>
                    )}

                    {rows.map((refund) => (
                      <RefundRow
                        key={refund.id}
                        refund={refund}
                        mayManage={mayManage}
                        expanded={expanded === refund.id}
                        onToggle={() => setExpanded(expanded === refund.id ? null : refund.id)}
                      />
                    ))}
                  </tbody>
                </TableWrap>
                {listMeta && (
                  <Pager
                    currentPage={listMeta.currentPage}
                    lastPage={listMeta.lastPage}
                    onChange={(page) => {
                      setListPage(page)
                      setExpanded(null)
                    }}
                  />
                )}
              </div>
            )}
          </Card>

          {report.data && <Coverage coverage={report.data.coverage} />}
        </>
      )}
    </>
  )
}

/**
 * The trend as one bar per period.
 *
 * Bars are scaled against the largest period rather than an absolute axis, and
 * the figures are in the tooltip and the table above — this is for seeing
 * whether refunds are rising, which a column of currency values genuinely does
 * not show. The firm-caused portion is drawn inside each bar in a hotter
 * colour, so a quarter where the total held steady while the firm's share grew
 * is visible as a shape rather than only as arithmetic.
 *
 * Empty periods are drawn as empty rather than skipped. A quarter with no
 * refunds is the most informative bar on this chart, and a line through only
 * the months that had one turns a gap into a slope.
 */
function TrendChart({ points }: { points: RefundTrendPoint[] }) {
  const peak = Math.max(0, ...points.map((point) => point.refundedMinor))

  if (points.length === 0) {
    return <p className="text-sm text-ink-600">No periods in this window.</p>
  }

  if (peak === 0) {
    return (
      <p className="text-sm text-ink-600">
        Nothing was refunded in this window. The bars are omitted rather than drawn flat, because a
        row of zero-height bars reads as a chart that failed to load.
      </p>
    )
  }

  return (
    <>
      {/*
        * `items-stretch`, not `items-end`. Each column is a full-height flex
        * box that pushes its bar to the bottom with `justify-end`; letting the
        * row shrink columns to their content would leave every bar sizing its
        * percentage height against a parent of zero, and the chart renders
        * empty with no error anywhere.
        */}
      <div className="flex h-40 items-stretch gap-1" role="img" aria-label="Refunds by period">
        {points.map((point) => {
          const share = (point.refundedMinor / peak) * 100
          const firmShare =
            point.refundedMinor > 0 ? (point.firmAttributedMinor / point.refundedMinor) * 100 : 0

          return (
            <div
              key={point.periodStart}
              className="flex min-w-0 flex-1 flex-col justify-end"
              title={`${point.periodStart} — ${formatMoney(point.refundedMinor)} across ${
                point.refundedCount
              }, of which ${formatMoney(point.firmAttributedMinor)} firm caused${
                point.refundRateBps === null
                  ? '. No receipts in this period.'
                  : `. ${formatBps(point.refundRateBps)} of receipts.`
              }`}
            >
              <div
                className="flex flex-col justify-end rounded-t-sm bg-brand-500"
                style={{ height: `${Math.max(share, share > 0 ? 2 : 0)}%` }}
              >
                {firmShare > 0 && (
                  <div className="rounded-t-sm bg-red-500" style={{ height: `${firmShare}%` }} />
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div className="mt-2 flex items-center justify-between text-xs text-ink-600">
        <span>{points[0]?.periodStart}</span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <span aria-hidden="true" className="h-2 w-2 rounded-full bg-brand-500" />
            Refunded
          </span>
          <span className="flex items-center gap-1.5">
            <span aria-hidden="true" className="h-2 w-2 rounded-full bg-red-500" />
            Firm caused
          </span>
        </span>
        <span>{points[points.length - 1]?.periodEnd}</span>
      </div>
    </>
  )
}

/**
 * Where refunds cluster (KAN-95: "surfaced, not left for the viewer to
 * eyeball").
 *
 * Written as a sentence of arithmetic a reader can redo — "2 of 9 attorneys
 * account for 71% of refunds" — rather than as a score. Nothing here is
 * weighted, and the rule that produced the flags is stated underneath for the
 * same reason the AR risk rule travels in the query string: a threshold nobody
 * can see is a threshold nobody can argue with.
 *
 * The card is rendered even when nothing is flagged. "No group carries a
 * disproportionate share" is the answer to the question, and a card that
 * appears only on bad news trains people to read its absence as not having
 * looked.
 */
function Concentration({ concentration }: { concentration: RefundConcentration }) {
  const noun = concentration.grouping === 'attorney' ? 'attorneys' : 'matter types'
  const { flagged, groupCount } = concentration

  return (
    <Card className="mb-6">
      <CardHeader
        title="Concentration"
        description={`Whether refunds cluster on particular ${noun}, or spread evenly across them.`}
        actions={
          <Badge tone={flagged.length > 0 ? 'critical' : 'positive'}>
            {flagged.length > 0 ? `${flagged.length} flagged` : 'Evenly spread'}
          </Badge>
        }
      />
      <CardBody>
        {groupCount < 2 ? (
          <p className="text-sm text-ink-600">
            Only one {noun.slice(0, -1)} refunded anything in this window, so there is nothing to
            concentrate against. Shown as unanswerable rather than as 100%.
          </p>
        ) : flagged.length === 0 ? (
          <p className="text-sm text-ink-600">
            No {noun.slice(0, -1)} carries a disproportionate share. Refunds are spread across{' '}
            {formatCount(groupCount)} {noun} without any one of them standing out under the rule
            below.
          </p>
        ) : (
          <>
            <p className="text-sm text-ink-800">
              <strong>
                {flagged.length} of {formatCount(groupCount)} {noun}
              </strong>{' '}
              account for {formatBps(concentration.flaggedShareBps)} of everything refunded in this
              window.
            </p>
            <ul className="mt-3 space-y-1.5">
              {flagged.map((entry) => (
                <li
                  key={entry.groupId ?? '__unattributed__'}
                  className="flex flex-wrap items-baseline gap-x-2 text-sm"
                >
                  <span className="font-medium text-ink-900">{entry.groupLabel}</span>
                  <span className="tabular-nums text-ink-700">{formatBps(entry.shareBps)}</span>
                  <span className="text-xs text-ink-600">
                    across {entry.refundCount} {entry.refundCount === 1 ? 'refund' : 'refunds'}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}

        <p className="mt-4 text-xs leading-relaxed text-ink-600">
          Flagged when a {noun.slice(0, -1)} holds at least{' '}
          {concentration.rule.minShareMultiple}× an even share — with{' '}
          {formatCount(groupCount)} {noun} refunding, that is{' '}
          {formatBps((10_000 / Math.max(groupCount, 1)) * concentration.rule.minShareMultiple)} — and
          has at least {concentration.rule.minRefundCount} refunds behind it. The count matters:
          one refund is an event, not a pattern, and without it a single large refund would flag its{' '}
          {noun.slice(0, -1)} every time. No weighted score is computed anywhere here.
        </p>
      </CardBody>
    </Card>
  )
}

function GroupRow({
  line,
  grouping,
  onFocus,
}: {
  line: RefundLine
  grouping: RefundGrouping
  onFocus?: () => void
}) {
  const label = groupLabelFor(line, grouping)

  return (
    <tr>
      <Td>
        <span className="block text-ink-900">{label}</span>
        <span className="block text-xs text-ink-600">
          {subtitleFor(line, grouping)}
          {line.refundedCount > 0 && (
            <>
              {subtitleFor(line, grouping) && ' · '}
              {line.refundedCount} {line.refundedCount === 1 ? 'refund' : 'refunds'}
            </>
          )}
        </span>
      </Td>
      <Td className="text-right tabular-nums">
        {formatMoney(line.refundedMinor)}
        {line.byKind.trust > 0 && line.byKind.billing_credit > 0 && (
          <span className="block text-xs text-ink-600">
            {formatMoney(line.byKind.trust)} from trust
          </span>
        )}
      </Td>
      <Td className="text-right tabular-nums">
        {line.refundedMinor > 0 ? (
          <>
            {formatBps(line.shareBps)}
            {line.concentrated && (
              <span className="mt-1 block">
                <Badge tone="critical">Concentrated</Badge>
              </span>
            )}
          </>
        ) : (
          <span className="text-ink-500">—</span>
        )}
      </Td>
      <Td className="text-right tabular-nums">
        {line.firmAttributedMinor > 0 ? (
          <span className="text-red-700">{formatMoney(line.firmAttributedMinor)}</span>
        ) : (
          <span className="text-ink-500">—</span>
        )}
      </Td>
      <Td className="text-right tabular-nums">
        {line.outstandingMinor > 0 ? (
          <>
            {formatMoney(line.outstandingMinor)}
            <span className="block text-xs text-ink-600">
              {line.outstandingCount} not yet processed
            </span>
          </>
        ) : (
          <span className="text-ink-500">—</span>
        )}
      </Td>
      <Td>
        {line.lastRefundAt ? (
          formatRelative(line.lastRefundAt)
        ) : (
          <span className="text-xs text-ink-500">Nothing settled</span>
        )}
      </Td>
      <Td>
        <span className="flex flex-wrap gap-1">
          {line.unclassifiedCount > 0 && (
            <Badge tone="neutral">{line.unclassifiedCount} unclassified</Badge>
          )}
          {line.unapprovedCount > 0 && (
            <Badge tone="warning">{line.unapprovedCount} unapproved</Badge>
          )}
          {line.unclassifiedCount === 0 && line.unapprovedCount === 0 && (
            <span className="text-xs text-ink-500">—</span>
          )}
        </span>
      </Td>
      {onFocus && (
        <Td className="text-right">
          {line.groupId ? (
            <Button size="sm" variant="ghost" onClick={onFocus}>
              View refunds
            </Button>
          ) : (
            <span className="text-xs text-ink-500">Unattributed</span>
          )}
        </Td>
      )}
    </tr>
  )
}

function RefundRow({
  refund,
  mayManage,
  expanded,
  onToggle,
}: {
  refund: ClientRefund
  mayManage: boolean
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <>
      <tr className={expanded ? 'bg-brand-50/50' : undefined}>
        <Td>
          <span className="block text-ink-900">{refund.clientName ?? 'Unknown client'}</span>
          <span className="block text-xs text-ink-600">
            {[refund.matterDisplayNumber, refund.matterType, refund.attorneyName]
              .filter(Boolean)
              .join(' · ') || 'No matter'}
          </span>
        </Td>
        <Td className="text-right tabular-nums">
          {formatMoney(refund.amountMinor)}
          {/*
            * Only trust is marked. Badging both kinds would give every row a
            * chip and make the column unscannable, which is the opposite of
            * "visually distinguished".
            */}
          {refund.refundKind === 'trust' && (
            <span
              className="mt-1 block"
              title={REFUND_KIND_META.trust.description}
            >
              <Badge tone={KIND_TONES.trust}>{REFUND_KIND_META.trust.short}</Badge>
            </span>
          )}
        </Td>
        <Td>
          <span title={REFUND_REASON_META[refund.reason].description}>
            <Badge tone={reasonTone(refund.reason)}>
              {REFUND_REASON_META[refund.reason].label}
            </Badge>
          </span>
        </Td>
        <Td>
          <span title={REFUND_STATUS_META[refund.status].description}>
            <Badge tone={STATUS_TONES[refund.status]}>
              {REFUND_STATUS_META[refund.status].label}
            </Badge>
          </span>
        </Td>
        <Td>
          {refund.decidedByName ? (
            <>
              <span className="block text-ink-800">{refund.decidedByName}</span>
              <span className="block text-xs text-ink-600">
                {formatRelative(refund.decidedAt)}
                {/*
                  * The band it was decided at, against the threshold in force
                  * then — not recomputed, so a policy change never restates
                  * what level a past approval was given at.
                  */}
                {refund.approvalTier === 'elevated' && (
                  <>
                    {' · '}
                    <span
                      className="text-accent-700"
                      title={REFUND_APPROVAL_TIER_META.elevated.description}
                    >
                      {REFUND_APPROVAL_TIER_META.elevated.label}
                      {refund.tierThresholdMinor !== null &&
                        ` (over ${formatMoney(refund.tierThresholdMinor)})`}
                    </span>
                  </>
                )}
              </span>
            </>
          ) : refund.status === 'completed' ? (
            /*
             * Said rather than left blank. An empty cell reads as missing data;
             * this is a fact about the refund — it went out without passing
             * through this platform, which is what a Clio-issued refund does.
             */
            <span className="text-xs text-ink-500">Not approved here</span>
          ) : (
            <span className="text-xs text-ink-500">—</span>
          )}
        </Td>
        <Td className="whitespace-nowrap">
          {refund.occurredAt ? (
            formatDateTime(refund.occurredAt)
          ) : (
            <>
              <span className="block text-ink-800">{formatDateTime(refund.requestedAt)}</span>
              <span className="block text-xs text-ink-600">recorded, not processed</span>
            </>
          )}
        </Td>
        <Td className="text-right">
          <Button size="sm" variant="ghost" onClick={onToggle} aria-expanded={expanded}>
            {expanded ? 'Hide' : 'Open'}
          </Button>
        </Td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={7} className="border-b border-ink-200 bg-ink-50 px-5 py-4">
            <RefundDetail refund={refund} mayManage={mayManage} />
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * The drill-down: what the refund came off, and the decision controls.
 *
 * Loaded on expand rather than with the page — a year of refunds would
 * otherwise fetch every invoice and trust movement behind them to show one.
 */
function RefundDetail({ refund, mayManage }: { refund: ClientRefund; mayManage: boolean }) {
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')
  const [reason, setReason] = useState<RefundReason>(refund.reason)

  const detail = useQuery({
    queryKey: ['refund', refund.id],
    queryFn: () => apiFetch<{ refund: ClientRefund; origin: RefundOrigin }>(`/refunds/${refund.id}`),
  })

  const update = useMutation({
    mutationFn: (body: { decision?: RefundDecision; reason?: RefundReason; note?: string }) =>
      apiFetch<ClientRefund>(`/refunds/${refund.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setNote('')
      queryClient.invalidateQueries({ queryKey: ['refunds'] })
      queryClient.invalidateQueries({ queryKey: ['refunds-list'] })
      queryClient.invalidateQueries({ queryKey: ['refund', refund.id] })
    },
  })

  const origin = detail.data?.origin

  return (
    <div className="space-y-4">
      {detail.isPending && <p className="text-sm text-ink-600">Loading the records behind it…</p>}
      {detail.error && <Alert tone="error">{messageFor(detail.error)}</Alert>}

      {origin && !origin.invoice && !origin.trustEntry && (
        <Alert tone="info">
          Nothing upstream is attached to this refund. It was recorded here without an invoice or a
          trust movement named, so there is nothing to drill into yet — the link appears when the
          matching movement arrives from Clio.
        </Alert>
      )}

      {origin?.trustEntry && (
        <div className="rounded-md border border-ink-200 bg-white p-4">
          <h4 className="font-display text-sm font-semibold text-brand-900">
            Trust movement it was paid from
          </h4>
          <dl className="mt-2 grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
            <Detail label="Amount" value={formatMoney(origin.trustEntry.amountMinor)} />
            <Detail
              label="Balance after"
              value={formatMoney(origin.trustEntry.balanceAfterMinor)}
            />
            <Detail label="Value date" value={formatDateTime(origin.trustEntry.occurredAt)} />
            <Detail
              label="Source"
              value={`${origin.trustEntry.sourceSystem} ${origin.trustEntry.sourceId ?? ''}`.trim()}
            />
          </dl>
          {origin.trustEntry.description && (
            <p className="mt-2 text-xs text-ink-600">{origin.trustEntry.description}</p>
          )}
          <p className="mt-2 text-xs text-ink-500">
            The ledger holds this movement negative, because there the sign is the direction. The
            refund above is positive: what went back.
          </p>
        </div>
      )}

      {origin?.invoice && (
        <div className="rounded-md border border-ink-200 bg-white p-4">
          <h4 className="font-display text-sm font-semibold text-brand-900">
            Invoice it came off
          </h4>
          <dl className="mt-2 grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
            <Detail label="Number" value={origin.invoice.number} />
            <Detail label="Status" value={origin.invoice.status} />
            <Detail label="Billed" value={formatMoney(origin.invoice.totalMinor)} />
            <Detail label="Outstanding" value={formatMoney(origin.invoice.balanceMinor)} />
          </dl>

          {origin.payments.length > 0 && (
            <>
              <p className="mt-3 text-[11px] font-semibold tracking-[0.12em] text-ink-500 uppercase">
                Received against it
              </p>
              <ul className="mt-1 space-y-0.5 text-xs tabular-nums text-ink-700">
                {origin.payments.map((payment) => (
                  <li key={payment.id}>
                    {formatMoney(payment.amountMinor)}
                    <span className="text-ink-600"> · {formatDateTime(payment.receivedAt)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="rounded-md border border-ink-200 bg-white p-4">
        <h4 className="font-display text-sm font-semibold text-brand-900">Record</h4>
        <dl className="mt-2 grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
          <Detail
            label="Recorded by"
            value={refund.requestedByName ?? 'Arrived from Clio'}
          />
          <Detail label="Recorded" value={formatDateTime(refund.requestedAt)} />
          <Detail label="Decided by" value={refund.decidedByName ?? '—'} />
          <Detail
            label="Source"
            value={`${refund.sourceSystem} ${refund.sourceId ?? ''}`.trim()}
          />
        </dl>
        {refund.reasonNote && <p className="mt-2 text-xs text-ink-600">{refund.reasonNote}</p>}
        {refund.decisionNote && (
          <p className="mt-1 text-xs text-ink-600">Decision: {refund.decisionNote}</p>
        )}
      </div>

      {mayManage && (
        <div className="rounded-md border border-ink-200 bg-white p-4">
          {update.error && (
            <Alert tone="error" className="mb-3">
              {messageFor(update.error)}
            </Alert>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              id={`reason-${refund.id}`}
              label="Why it happened"
              hint={REFUND_REASON_META[reason].description}
            >
              <Select
                id={`reason-${refund.id}`}
                value={reason}
                onChange={(event) => setReason(event.target.value as RefundReason)}
              >
                {REFUND_REASONS.map((option) => (
                  <option key={option} value={option}>
                    {REFUND_REASON_META[option].label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              id={`note-${refund.id}`}
              label="Note"
              hint={
                refund.status === 'pending_approval'
                  ? 'Recorded against the decision. Optional, and the thing that makes this queue readable a year from now.'
                  : 'Recorded against the reason.'
              }
            >
              <TextInput
                id={`note-${refund.id}`}
                value={note}
                maxLength={1000}
                placeholder="Optional"
                onChange={(event) => setNote(event.target.value)}
              />
            </Field>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {refund.status === 'pending_approval' ? (
              REFUND_DECISIONS.map((decision) => (
                <Button
                  key={decision}
                  variant={decision === 'approved' ? 'accent' : 'danger'}
                  size="sm"
                  disabled={update.isPending}
                  onClick={() =>
                    update.mutate({
                      decision,
                      reason: reason === refund.reason ? undefined : reason,
                      note: note.trim() || undefined,
                    })
                  }
                >
                  {REFUND_STATUS_META[decision].label}
                </Button>
              ))
            ) : (
              <p className="text-xs text-ink-600">
                {refund.status === 'completed'
                  ? 'Already processed, so there is no decision left to make — approving money that has gone would not be an approval. Classifying it still helps: that is what the trend is built from.'
                  : 'Already decided. The reason can still be corrected.'}
              </p>
            )}

            <Button
              size="sm"
              disabled={update.isPending || (reason === refund.reason && note.trim() === '')}
              onClick={() =>
                update.mutate({ reason, note: note.trim() || undefined })
              }
            >
              Save the reason
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Recording a refund so somebody can approve it before it is processed.
 *
 * It is created `pending_approval` and can never complete itself — the money
 * leaving is evidenced upstream or not at all. Ids are entered rather than
 * picked from a list because there is no client lookup endpoint in this
 * platform yet, and inventing one for this form would be a larger change than
 * the feature; the fields say where to find them.
 */
function RecordRefund({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient()
  const [clientId, setClientId] = useState('')
  const [matterId, setMatterId] = useState('')
  const [invoiceId, setInvoiceId] = useState('')
  const [amount, setAmount] = useState('')
  /**
   * Defaults to trust because that is what the overwhelming majority of a
   * retainer firm's refunds are — and the endpoint requires it either way, so
   * this is a sensible starting position rather than a value somebody can skip
   * past without noticing.
   */
  const [refundKind, setRefundKind] = useState<RefundKind>('trust')
  const [reason, setReason] = useState<RefundReason>('unearned_retainer')
  const [note, setNote] = useState('')

  const record = useMutation({
    mutationFn: () =>
      apiFetch<ClientRefund>('/refunds', {
        method: 'POST',
        body: JSON.stringify({
          clientId: clientId.trim(),
          matterId: matterId.trim() || undefined,
          invoiceId: invoiceId.trim() || undefined,
          /**
           * Dollars in the form, minor units on the wire — and rounded here
           * rather than truncated, so a figure typed with a stray third decimal
           * lands on the nearest cent instead of losing one.
           */
          amountMinor: Math.round(Number(amount) * 100),
          refundKind,
          reason,
          note: note.trim() || undefined,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refunds'] })
      queryClient.invalidateQueries({ queryKey: ['refunds-list'] })
      onDone()
    },
  })

  const parsed = Number(amount)
  const validAmount = amount.trim() !== '' && Number.isFinite(parsed) && parsed > 0

  return (
    <Card className="mb-6">
      <CardHeader
        title="Record a refund for approval"
        description="Creates it pending, with no money moved. It completes when the matching refund arrives from Clio, which is the only evidence that it was actually paid."
      />
      <CardBody>
        {record.error && (
          <Alert tone="error" className="mb-3">
            {messageFor(record.error)}
          </Alert>
        )}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Field id="new-client" label="Client id" hint="This platform's id, not Clio's.">
            <TextInput
              id="new-client"
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
            />
          </Field>
          <Field id="new-matter" label="Matter id" hint="Optional. Sets the responsible attorney.">
            <TextInput
              id="new-matter"
              value={matterId}
              onChange={(event) => setMatterId(event.target.value)}
            />
          </Field>
          <Field id="new-invoice" label="Invoice id" hint="Optional. Leave blank for a trust refund.">
            <TextInput
              id="new-invoice"
              value={invoiceId}
              onChange={(event) => setInvoiceId(event.target.value)}
            />
          </Field>
          <Field id="new-amount" label="Amount" hint="US dollars. Positive: what goes back.">
            <TextInput
              id="new-amount"
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </Field>
          <Field
            id="new-kind"
            label="Paid from"
            hint={REFUND_KIND_META[refundKind].description}
          >
            <Select
              id="new-kind"
              value={refundKind}
              onChange={(event) => setRefundKind(event.target.value as RefundKind)}
            >
              {REFUND_KINDS.map((option) => (
                <option key={option} value={option}>
                  {REFUND_KIND_META[option].label}
                </option>
              ))}
            </Select>
          </Field>
          <Field id="new-reason" label="Reason" hint={REFUND_REASON_META[reason].description}>
            <Select
              id="new-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value as RefundReason)}
            >
              {REFUND_REASONS.filter((option) => option !== 'unclassified').map((option) => (
                <option key={option} value={option}>
                  {REFUND_REASON_META[option].label}
                </option>
              ))}
            </Select>
          </Field>
          <Field id="new-note" label="Note" hint="Optional. What this was actually about.">
            <TextInput
              id="new-note"
              value={note}
              maxLength={1000}
              onChange={(event) => setNote(event.target.value)}
            />
          </Field>
        </div>

        <div className="mt-4 flex gap-2">
          <Button
            variant="accent"
            disabled={!clientId.trim() || !validAmount || record.isPending}
            onClick={() => record.mutate()}
          >
            {record.isPending ? 'Recording…' : 'Record it'}
          </Button>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}

/**
 * What this dashboard can and cannot see, from the API rather than hard-coded
 * here — which sources are configured is a fact about the deployment, and a
 * caveat the client asserted on its own would go stale the day that changed.
 * It already has once: these lines named QuickBooks as unreachable for as long
 * as it was, and the screen needed no change on the day it was connected.
 */
function Coverage({ coverage }: { coverage: RefundCoverage }) {
  return (
    <div className="mt-6 max-w-3xl border-t border-ink-200 pt-5 text-xs leading-relaxed text-ink-600">
      <p className="font-semibold text-ink-700">What is on this page</p>
      <ul className="mt-1 list-disc space-y-1 pl-5">
        {coverage.covered.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      {coverage.uncovered.length > 0 && (
        <>
          <p className="mt-3 font-semibold text-ink-700">And what is not</p>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            {coverage.uncovered.map((entry) => (
              <li key={entry.source}>
                <span className="font-medium">{entry.source}</span> — {entry.reason}
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="mt-3">
        Amounts here are positive: what went back. The same trust movement is negative in the
        ledger, where the sign is the direction. A refund recorded on this platform is never counted
        as money returned until an upstream record of it exists — which is why the committed column
        and the refunded column are never added together.
      </p>
    </div>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold tracking-[0.12em] text-ink-500 uppercase">
        {label}
      </dt>
      <dd className="mt-0.5 text-ink-800">{value || '—'}</dd>
    </div>
  )
}

/** What a row is, beyond its own name, under each grouping. */
/** The reason grouping labels itself from the shared metadata, not the id. */
function groupLabelFor(line: RefundLine, grouping: RefundGrouping): string {
  return grouping === 'reason' && isReason(line.groupLabel)
    ? REFUND_REASON_META[line.groupLabel].label
    : line.groupLabel
}

/** One tab in the strip above the refund tables, underlined when it is the one showing. */
function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`-mb-px flex items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
        active
          ? 'border-brand-700 text-ink-900'
          : 'border-transparent text-ink-600 hover:border-ink-300 hover:text-ink-900'
      }`}
    >
      {children}
    </button>
  )
}

/** Search and filter controls sitting between a card's header and its table. */
function Toolbar({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-ink-100 px-5 py-3">
      {children}
    </div>
  )
}

/** Previous and next under a table, matching the audit log's. Hidden on a single page. */
function Pager({
  currentPage,
  lastPage,
  onChange,
}: {
  currentPage: number
  lastPage: number
  onChange: (page: number) => void
}) {
  if (lastPage <= 1) return null

  return (
    <div className="flex items-center justify-between gap-3 border-t border-ink-100 px-5 py-3">
      <p className="text-sm text-ink-600">
        Page {currentPage} of {lastPage}
      </p>
      <div className="flex gap-2">
        <Button size="sm" disabled={currentPage <= 1} onClick={() => onChange(currentPage - 1)}>
          Previous
        </Button>
        <Button
          size="sm"
          disabled={currentPage >= lastPage}
          onClick={() => onChange(currentPage + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

function subtitleFor(line: RefundLine, grouping: RefundGrouping): string {
  if (grouping === 'client') return line.attorneyName ?? ''
  if (grouping === 'matter') return [line.clientName, line.attorneyName].filter(Boolean).join(' · ')
  if (grouping === 'reason') return REFUND_REASON_META[line.groupLabel as RefundReason]?.description ?? ''
  return line.clientName ?? ''
}

function isReason(value: string): value is RefundReason {
  return (REFUND_REASONS as readonly string[]).includes(value)
}

function messageFor(error: unknown): string {
  return error instanceof ApiError
    ? `${error.message} (${error.status})`
    : 'Something went wrong. Try again.'
}
